import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Code, Calendar, RefreshCw, Zap, Lock } from 'lucide-react';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);

  const plans = {
    starter: {
      priceId: 'price_starter_plan_id',
      name: 'Starter'
    },
    professional: {
      priceId: 'price_professional_plan_id',
      name: 'Professional'
    },
    business: {
      priceId: 'price_business_plan_id',
      name: 'Business'
    },
    enterprise: {
      priceId: 'price_enterprise_plan_id',
      name: 'Enterprise'
    }
  };

  const createCheckout = async (planKey: string) => {
    // Redirect users to register first, then use dashboard for upgrades
    if (user) {
      // User is logged in, redirect to dashboard
      window.location.href = '/dashboard';
    } else {
      // User not logged in, redirect to registration
      window.location.href = '/register';
    }
  };

  return (
    <div className="landing-page">
      {/* Modern Navigation */}
      <header className="modern-nav">
        <nav className="nav-content">
          <Link to="/" className="nav-logo">
            Pfändungstabelle API
          </Link>
          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#pricing" className="nav-link">Preise</a>
            <a href="#docs" className="nav-link">Dokumentation</a>
            <a href="#contact" className="nav-link">Kontakt</a>
            {user ? (
              <Link to="/dashboard" className="nav-cta">
                Dashboard
              </Link>
            ) : (
              <Link to="/login" className="nav-cta">
                Anmelden
              </Link>
            )}
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Pfändungstabelle API
          </h1>
          <p className="hero-subtitle">
            Immer aktuelle Pfändungsfreigrenzen
          </p>
          <p className="hero-description">
            Offizielle deutsche Pfändungstabellen als JSON-API. Automatisch aktualisiert, rechtssicher, developer-friendly.
          </p>
          <a href="#pricing" className="hero-cta">
            API jetzt testen
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="section-title">
            Schluss mit manueller PDF-Extraktion
          </h2>
          <div className="problem-grid">
            <div className="problem-card">
              <h3 className="text-xl font-semibold text-red-600 mb-4">Manuelle PDF-Verarbeitung</h3>
              <p className="text-gray-600">
                Offizielle Tabellen werden als PDFs veröffentlicht und erfordern manuelle Extraktion
              </p>
            </div>
            <div className="problem-card">
              <h3 className="text-xl font-semibold text-red-600 mb-4">Veraltete Daten</h3>
              <p className="text-gray-600">
                Keine automatisierte Möglichkeit, aktuelle Pfändungsfreigrenzen sicherzustellen
              </p>
            </div>
            <div className="problem-card">
              <h3 className="text-xl font-semibold text-red-600 mb-4">Zeitaufwändige Recherche</h3>
              <p className="text-gray-600">
                Anwälte verbringen wertvolle Zeit mit Datensuche statt mit Rechtsarbeit
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="features-container">
          <h2 className="section-title">
            Entwicklerfreundliche Integration
          </h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Shield size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Offizielle BMJ-Daten</h3>
              <p className="text-gray-600">Basiert auf offiziellen Daten des Bundesjustizministeriums</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Code size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">JSON Format</h3>
              <p className="text-gray-600">Entwicklerfreundliches Format für einfache Integration</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Calendar size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Alle Zeiträume</h3>
              <p className="text-gray-600">Monatliche, wöchentliche und tägliche Tabellen verfügbar</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <RefreshCw size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">Automatische Updates</h3>
              <p className="text-gray-600">Automatische Aktualisierung bei neuen Freigrenzen</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Zap size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">99.9% Uptime</h3>
              <p className="text-gray-600">Zuverlässige Verfügbarkeit über Cloudflare Global Network</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Lock size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2">DSGVO-konform</h3>
              <p className="text-gray-600">Datenschutzkonforme Verarbeitung und Speicherung</p>
            </div>
          </div>
        </div>
      </section>

      {/* API Example Section */}
      <section id="docs" className="api-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="section-title white">
            Einfache API-Integration
          </h2>
          <div className="space-y-8">
            <div className="terminal-window">
              <div className="terminal-header">
                <div className="terminal-dot red"></div>
                <div className="terminal-dot yellow"></div>
                <div className="terminal-dot green"></div>
                <div className="terminal-title">API Request</div>
              </div>
              <div className="terminal-content">
                <pre>
<span className="terminal-prompt">$</span> <span className="terminal-command">curl -H "Authorization: Bearer YOUR_API_KEY" \</span>
     <span className="terminal-command">"https://api.pfaendungstabelle.voidmind.io/v1/data?year=2025"</span>

<span className="terminal-output">{`{
  "success": true,
  "data": {
    "year": 2025,
    "last_updated": "2025-01-01T00:00:00Z",
    "tables": {
      "monatlich": [
        {
          "nettolohn_min": 1402.28,
          "nettolohn_max": 1499.99,
          "unterhaltspflicht": {
            "0": 0,
            "1": 0,
            "2": 0,
            "3": 0,
            "4": 0,
            "5_plus": 0
          }
        }
      ],
      "woechentlich": [...],
      "taeglich": [...]
    }
  }
}`}</span>
                </pre>
              </div>
            </div>
            
            <div className="terminal-window">
              <div className="terminal-header">
                <div className="terminal-dot red"></div>
                <div className="terminal-dot yellow"></div>
                <div className="terminal-dot green"></div>
                <div className="terminal-title">Python Example</div>
              </div>
              <div className="terminal-content">
                <pre>
<span className="terminal-comment"># Python Beispiel für die Pfändungstabelle API</span>
<span className="terminal-output">{`
import requests
headers = {"Authorization": "Bearer YOUR_API_KEY"}

# Verfügbare Jahre abrufen
years_response = requests.get(
    "https://api.pfaendungstabelle.voidmind.io/v1/years",
    headers=headers
)
available_years = years_response.json()

# Pfändungstabelle für ein Jahr abrufen
response = requests.get(
    "https://api.pfaendungstabelle.voidmind.io/v1/data?year=2025",
    headers=headers
)
data = response.json()

# Pfändungsbetrag berechnen
calculation_data = {
    "netIncome": 2500.00,
    "dependents": 2,
    "period": "monthly",
    "year": 2025
}
calc_response = requests.post(
    "https://api.pfaendungstabelle.voidmind.io/v1/calculate",
    headers=headers,
    json=calculation_data
)`}</span>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="pricing-container">
          <h2 className="section-title">
            Transparente Preise
          </h2>
          <div className="pricing-grid">
            {/* Starter Plan */}
            <div className="pricing-card">
              <div className="text-2xl font-bold mb-2">Starter</div>
              <div className="text-4xl font-bold text-blue-600 mb-2">€9</div>
              <div className="text-gray-600 mb-8">pro Monat</div>
              <ul className="space-y-4 mb-8 text-left">
                <li className="border-b border-gray-100 pb-2">500 API Calls/Monat</li>
                <li className="border-b border-gray-100 pb-2">Aktuelles + letztes Jahr</li>
                <li className="border-b border-gray-100 pb-2">JSON Format</li>
                <li className="pb-2">Email Support</li>
              </ul>
              <button
                onClick={() => createCheckout('starter')}
                disabled={loading === 'starter'}
                className="pricing-cta"
              >
                {loading === 'starter' ? (
                  <span className="loading-spinner"></span>
                ) : (
                  'Jetzt starten'
                )}
              </button>
            </div>

            {/* Professional Plan */}
            <div className="pricing-card popular">
              <div className="popular-badge">Beliebt</div>
              <div className="text-2xl font-bold mb-2">Professional</div>
              <div className="text-4xl font-bold text-blue-600 mb-2">€29</div>
              <div className="text-gray-600 mb-8">pro Monat</div>
              <ul className="space-y-4 mb-8 text-left">
                <li className="border-b border-gray-100 pb-2">5.000 API Calls/Monat</li>
                <li className="border-b border-gray-100 pb-2">Daten ab 2021</li>
                <li className="border-b border-gray-100 pb-2">JSON + CSV Export</li>
                <li className="border-b border-gray-100 pb-2">Priority Support</li>
                <li className="pb-2">Webhook Notifications</li>
              </ul>
              <button
                onClick={() => createCheckout('professional')}
                disabled={loading === 'professional'}
                className="pricing-cta"
              >
                {loading === 'professional' ? (
                  <span className="loading-spinner"></span>
                ) : (
                  'Jetzt starten'
                )}
              </button>
            </div>

            {/* Business Plan */}
            <div className="pricing-card">
              <div className="text-2xl font-bold mb-2">Business</div>
              <div className="text-4xl font-bold text-blue-600 mb-2">€79</div>
              <div className="text-gray-600 mb-8">pro Monat</div>
              <ul className="space-y-4 mb-8 text-left">
                <li className="border-b border-gray-100 pb-2">50.000 API Calls/Monat</li>
                <li className="border-b border-gray-100 pb-2">Historische Daten ab 2005</li>
                <li className="border-b border-gray-100 pb-2">Alle Formate</li>
                <li className="border-b border-gray-100 pb-2">Phone + Email Support</li>
                <li className="border-b border-gray-100 pb-2">Excel Add-In</li>
                <li className="pb-2">99.9% SLA</li>
              </ul>
              <button
                onClick={() => createCheckout('business')}
                disabled={loading === 'business'}
                className="pricing-cta"
              >
                {loading === 'business' ? (
                  <span className="loading-spinner"></span>
                ) : (
                  'Jetzt starten'
                )}
              </button>
            </div>

            {/* Enterprise Plan */}
            <div className="pricing-card">
              <div className="text-2xl font-bold mb-2">Enterprise</div>
              <div className="text-2xl font-bold text-gray-600 mb-2 text-center">Individuell</div>
              <div className="text-gray-600 mb-8 text-center">nach Vereinbarung</div>
              <ul className="space-y-4 mb-8 text-left">
                <li className="border-b border-gray-100 pb-2">Unlimited API Calls</li>
                <li className="border-b border-gray-100 pb-2">Historische Daten ab 2005</li>
                <li className="border-b border-gray-100 pb-2">Dedicated Support</li>
                <li className="border-b border-gray-100 pb-2">Custom Integrations</li>
                <li className="border-b border-gray-100 pb-2">Advanced Analytics</li>
                <li className="pb-2">Custom SLA</li>
              </ul>
              <div className="text-center">
                <a
                  href="mailto:enterprise@pfaendungstabelle.voidmind.io?subject=Enterprise%20Anfrage"
                  className="pricing-cta inline-block text-center"
                >
                  Kontakt aufnehmen
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">
            Vertrauenswürdig & Rechtssicher
          </h2>
          <div className="trust-grid">
            <div className="trust-item">
              <span className="trust-check">✓</span>
              <span>Basiert auf offiziellen BMJ-Daten</span>
            </div>
            <div className="trust-item">
              <span className="trust-check">✓</span>
              <span>DSGVO-konform gehostet</span>
            </div>
            <div className="trust-item">
              <span className="trust-check">✓</span>
              <span>Keine Logs von API-Anfragen</span>
            </div>
            <div className="trust-item">
              <span className="trust-check">✓</span>
              <span>99.9% Uptime Garantie</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="modern-footer">
        <div className="max-w-7xl mx-auto text-center">
          <div className="footer-links">
            <Link to="/support" className="footer-link">Support</Link>
            <Link to="/privacy" className="footer-link">Datenschutz</Link>
            <Link to="/terms" className="footer-link">AGB</Link>
            <Link to="/imprint" className="footer-link">Impressum</Link>
          </div>
          <p>&copy; 2025 VoidMind. Alle Rechte vorbehalten.</p>
        </div>
      </footer>

      {/* Stripe Script */}
      <script src="https://js.stripe.com/v3/"></script>
    </div>
  );
};

export default LandingPage;
